import React, { useState, useEffect } from "react";
import "./App.css";
import ChatInterface from "./components/ChatInterface";
import ContactCard from "./components/ContactCard";
import heroBg from "./hero-bg.jpg";
import productImg from "./product.jpg";

// 导航栏组件
interface HeaderProps {
  onShowQRCode: () => void;
}

const Header: React.FC<HeaderProps> = ({ onShowQRCode }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  // 监听滚动事件，实现导航栏背景变化
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 平滑滚动到指定区域
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header className={`header ${isScrolled ? "scrolled" : ""}`}>
      <div className="header-content">
        <div className="logo-section">
          <h1 className="logo">黑夜智能</h1>
          <span className="slogan">专业的AI员工为您业务效能</span>
        </div>

        <nav className="nav-links">
          <button onClick={() => scrollToSection("home")} className="nav-link">
            首页
          </button>
          <button
            onClick={() => scrollToSection("solution")}
            className="nav-link"
          >
            解决方案
          </button>
          <button
            onClick={() => scrollToSection("features")}
            className="nav-link"
          >
            核心能力
          </button>
          <button onClick={() => scrollToSection("cases")} className="nav-link">
            客户案例
          </button>
          <button
            onClick={() => scrollToSection("contact")}
            className="nav-link"
          >
            联系我们
          </button>
        </nav>

        <button onClick={onShowQRCode} className="cta-button">
          申请体验
        </button>
      </div>
    </header>
  );
};

// Hero主视觉区域组件
interface HeroSectionProps {
  onStartChat: () => void;
  onShowQRCode: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  onStartChat,
  onShowQRCode,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIsVisible(true);

    // 光标闪烁效果
    const cursorInterval = setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, 800);

    return () => clearInterval(cursorInterval);
  }, []);

  // 拖拽开始处理函数
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  // 拖拽移动处理函数
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  // 拖拽结束处理函数
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      className="hero-section"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(102, 126, 234, 0.4) 0%, rgba(118, 75, 162, 0.4) 100%), url(${heroBg})`,
      }}
    >
      <div className="hero-content">
        <div className={`hero-left ${isVisible ? "fade-in" : ""}`}>
          <div className="greeting">
            <div className="greeting-icon">👋</div>
            <span>Hi～我是小迎</span>
          </div>

          <h1 className="hero-title">
            小迎家最智能的
            <br />
            <span className="highlight">AI</span>员工
          </h1>

          <p className="hero-subtitle">100+头部品牌的首选</p>

          <button onClick={onShowQRCode} className="hero-cta">
            申请体验
          </button>
        </div>

        <div
          className={`hero-right ${isVisible ? "slide-in" : ""}`}
          style={{ marginTop: "60px" }}
        >
          <div
            className={`ai-chat-card ${isDragging ? "dragging" : ""}`}
            onMouseDown={handleMouseDown}
            style={{
              transform: `translate(${position.x}px, ${position.y}px)`,
              cursor: isDragging ? "grabbing" : "grab",
              userSelect: "none",
            }}
          >
            <div className="ai-avatar">
              <img
                src="https://miaoduo.fbcontent.cn/private/resource/image/1995f9df7a39834-8f42c32f-e3f8-47ba-bd27-45c995b4781b.jpeg"
                alt="AI员工小迎"
              />
            </div>
            <h3>AI客服员工—小迎</h3>
            <p>
              你从回答客户常问问题到遵守平台规则，小迎bot都能轻松应对，一应俱全。
            </p>
            <div
              className="chat-bubble"
              onClick={onStartChat}
              style={{ cursor: "pointer" }}
            >
              <span>我今天能为你做些什么？</span>
              <span className={`cursor ${cursorVisible ? "visible" : ""}`}>
                |
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 解决方案区域组件
const SolutionSection: React.FC = () => {
  const [cardsVisible, setCardsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCardsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById("solution");
    if (section) {
      observer.observe(section);
    }

    return () => observer.disconnect();
  }, []);

  const handleCardClick = (targetId: string) => {
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const solutions = [
    {
      icon: "🧠",
      title: '"喂养式"知识大脑',
      subtitle: "让AI秒懂您的复杂业务",
      targetId: "solution-expert",
    },
    {
      icon: "🎯",
      title: '"一体化"作战指挥室',
      subtitle: "无缝融合AI与人工，告别信息孤岛",
      targetId: "solution-team",
    },
    {
      icon: "⚡",
      title: '"指令式"AI执行官',
      subtitle: '用自然语言精准控制AI，杜绝"乱说话"',
      targetId: "solution-auto",
    },
  ];

  return (
    <section id="solution" className="solution-section">
      <div className="solution-content">
        <div className="solution-header">
          <div className="solution-badge">
            <span className="badge-dot"></span>
            <span>AI员工管理平台</span>
          </div>
          <h2 className="solution-title">为业务增长</h2>
          <p className="solution-subtitle">
            通过AI员工管理平台，实现从客户获取到服务交付的全流程自动化，让您的业务永不停歇
          </p>
          <p className="solution-subtitle">
            使用小迎家的服务，客户可终身免费使用应用。
          </p>
        </div>

        <div className="solution-cards">
          {solutions.map((solution, index) => (
            <div
              key={index}
              className={`solution-card ${cardsVisible ? "animate-in" : ""}`}
              style={{ animationDelay: `${index * 0.2}s` }}
              onClick={() => handleCardClick(solution.targetId)}
            >
              <div className="card-icon">{solution.icon}</div>
              <h3>{solution.title}</h3>
              <p>{solution.subtitle}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 产品界面展示组件
interface ProductSectionProps {
  onStartChat: () => void;
  onShowQRCode: () => void;
}

const ProductSection: React.FC<ProductSectionProps> = ({
  onStartChat,
  onShowQRCode,
}) => {
  return (
    <section id="product" className="product-section">
      <div className="product-content">
        <div className="product-header">
          <h2 className="product-title">产品界面展示</h2>
          <p className="product-subtitle">
            我们的AI员工管理平台提供直观易用的管理界面
          </p>
        </div>

        <div className="product-image-container">
          <img src={productImg} alt="产品界面展示" className="product-image" />
        </div>

        <button className="try-button" onClick={onShowQRCode}>
          立即开始
        </button>
      </div>
    </section>
  );
};

// 核心能力数据展示组件
interface FeaturesSectionProps {
  onShowQRCode: () => void;
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({ onShowQRCode }) => {
  const [countersStarted, setCountersStarted] = useState(false);
  const [counts, setCounts] = useState({
    days: 0,
    accuracy: 0,
    growth: 0,
    automation: 0,
    solutions: 0,
    availability: 0,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !countersStarted) {
            setCountersStarted(true);
            animateCounters();
          }
        });
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById("features");
    if (section) {
      observer.observe(section);
    }

    return () => observer.disconnect();
  }, [countersStarted]);

  // 数字滚动动画
  const animateCounters = () => {
    const targets = {
      days: 1,
      accuracy: 95,
      growth: 3,
      automation: 80,
      solutions: 1000,
      availability: 24,
    };
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounts({
        days: Math.floor(targets.days * progress),
        accuracy: Math.floor(targets.accuracy * progress),
        growth: Math.floor(targets.growth * progress),
        automation: Math.floor(targets.automation * progress),
        solutions: Math.floor(targets.solutions * progress),
        availability: Math.floor(targets.availability * progress),
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setCounts(targets);
      }
    }, stepDuration);
  };

  const features = [
    {
      title: "1天内",
      subtitle: "快速上岗",
      description:
        "无需复杂的培训流程，只需简单配置就能快速上岗，立即开始为您的业务提供支持，提升团队工作效率。",
      value: counts.days,
      suffix: "天内",
      color: "#9333EA",
    },
    {
      title: "95%",
      subtitle: "业务问题解决率",
      description:
        "AI员工基于深度学习和知识图谱技术，能够准确理解并解决95%以上的常见业务问题，让您的客户获得专业服务。",
      value: counts.accuracy,
      suffix: "%",
      color: "#4F46E5",
    },
    {
      title: "3倍",
      subtitle: "效率提升",
      description:
        "通过智能化的工作流程和自动化处理，AI员工能够将工作效率提升3倍以上，让您的团队专注于更有价值的工作。",
      value: counts.growth,
      suffix: "倍",
      color: "#06B6D4",
    },
    {
      title: "80%+",
      subtitle: "重复劳动自动化",
      description:
        "智能识别并自动处理重复性工作，将80%以上的重复劳动实现自动化，释放人力资源投入到创新性工作中。",
      value: counts.automation,
      suffix: "%+",
      color: "#10B981",
    },
    {
      title: "1,000+",
      subtitle: "解决方案",
      description:
        "拥有超过1000种预设解决方案，覆盖各行各业的常见场景，无论您在哪个行业，都能找到适合的AI员工解决方案。",
      value: counts.solutions,
      suffix: "+",
      color: "#F59E0B",
    },
    {
      title: "24/7",
      subtitle: "全天候不间断",
      description:
        "AI员工全年无休，在您需要的任何时候都能提供服务，无论是深夜还是节假日，都能确保业务的连续性和客户满意度。",
      value: counts.availability,
      suffix: "/7",
      color: "#EF4444",
    },
  ];

  return (
    <section id="features" className="features-section">
      <div className="features-content">
        <h2 className="features-title">
          AI员工核心能力——极速上岗、精准转化、增长倍增
        </h2>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-header">
                <div className="feature-value" style={{ color: feature.color }}>
                  {feature.value}
                  {feature.suffix}
                </div>
                <div className="feature-subtitle">{feature.subtitle}</div>
              </div>
              <div className="feature-description">{feature.description}</div>
            </div>
          ))}
        </div>

        <div className="features-footer">
          <h3 className="features-summary-title">完全自主，终身学习</h3>
          <p className="features-summary">
            小迎家AI员工具备自主学习能力，可以根据业务场景持续优化表现。无需人工干预，就能不断提升服务质量，成为您业务增长的强大引擎。
          </p>
          <button className="features-cta-button" onClick={onShowQRCode}>
            立即开始
          </button>
        </div>
      </div>
    </section>
  );
};

// 场景化功能详述组件
const ScenarioSection: React.FC = () => {
  const scenarios = [
    {
      id: "solution-expert",
      title: "更懂业务的售前转化专家",
      description:
        "基于深度学习的业务理解能力，AI员工能够精准把握客户需求，提供个性化的解决方案，大幅提升转化率。让每一次客户接触都成为转化机会。",
      features: [
        {
          icon: "✅",
          text: "智能识别：深入分析客户，准确判断客户需求和购买意向",
        },
        {
          icon: "✅",
          text: "全渠道支持：上千种行业解决方案，覆盖各类业务场景",
        },
      ],
      tags: ["智能识别", "需求分析", "解决方案"],
      imageUrl:
        "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=500&h=300&fit=crop",
      reverse: false,
    },
    {
      id: "solution-team",
      title: "人机无缝，团队效率翻倍",
      description:
        "AI员工无缝融入现有团队，实现智能协作和任务分配。让人工团队专注于创新性工作，AI员工处理标准化流程，实现效率最大化。",
      features: [
        { icon: "✅", text: "无缝协作：融入现有工作流程，与团队成员高效协作" },
        { icon: "✅", text: "人机互补：基于AI的智能决策，结合人工的创新思维" },
      ],
      tags: ["无缝协作", "智能分配", "效率翻倍"],
      imageUrl:
        "https://images.unsplash.com/photo-1552664730-d307ca884978?w=500&h=300&fit=crop",
      reverse: true,
    },
    {
      id: "solution-auto",
      title: "一句话指令，自动处理繁琐事务",
      description:
        "通过自然语言理解技术，AI员工能够准确理解复杂指令，自动执行各类重复性工作。从数据整理到报告生成，一句话搞定所有繁琐事务。",
      features: [
        { icon: "✅", text: "智能理解：自然语言处理，准确理解复杂业务指令" },
        { icon: "✅", text: "高效执行：100%准确率的任务执行，工作效率不打折" },
      ],
      tags: ["自然语言", "智能执行", "自动化"],
      imageUrl:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&h=300&fit=crop",
      reverse: false,
    },
    {
      id: "solution-insight",
      title: "洞察客户需求，私域电商增长新机会",
      description:
        "深度分析客户行为数据，挖掘潜在商机。AI员工能够识别客户购买信号，预测市场趋势，为业务决策提供数据支撑，发现新的增长机会。",
      features: [
        { icon: "✅", text: "客户画像：基于数据分析，深度理解客户需求和偏好" },
        { icon: "✅", text: "商机识别：智能识别潜在商机，精准推荐客户需要" },
      ],
      tags: ["数据洞察", "智能分析", "增长机会"],
      imageUrl:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=300&fit=crop",
      reverse: true,
    },
  ];

  return (
    <section className="scenario-section">
      <div className="scenario-content">
        <h2 className="scenario-title">像专家一样思考，像团队一样协作</h2>
        <p className="scenario-subtitle">
          人工智能员工不仅是工具，更是您业务团队中不可或缺的智能伙伴
        </p>

        <div className="scenarios">
          {scenarios.map((scenario, index) => (
            <div
              key={scenario.id}
              id={scenario.id}
              className={`scenario-item ${scenario.reverse ? "reverse" : ""}`}
            >
              <div className="scenario-image">
                <img
                  src={scenario.imageUrl}
                  alt={scenario.title}
                  className="scenario-img"
                />
              </div>
              <div className="scenario-text">
                <h3>{scenario.title}</h3>
                <p className="scenario-description">{scenario.description}</p>

                <div className="scenario-features">
                  {scenario.features.map((feature, idx) => (
                    <div key={idx} className="scenario-feature">
                      <span className="feature-icon">{feature.icon}</span>
                      <span className="feature-text">{feature.text}</span>
                    </div>
                  ))}
                </div>

                <div className="scenario-tags">
                  {scenario.tags.map((tag, idx) => (
                    <span key={idx} className="scenario-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 客户案例组件
const CasesSection: React.FC = () => {
  const [currentCase, setCurrentCase] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  const cases = [
    {
      name: "陈雨",
      title: "某知名美妆品牌私域负责人",
      content:
        "AI员工帮助我们的私域转化率提升了7.3倍，客户满意度达到了98%，现在每天都有大量精准客户主动下单，真的太棒了！",
      avatar: "👩‍💼",
    },
    {
      name: "李明",
      title: "服装电商创始人",
      content:
        "接入AI员工后，我们的私域运营效率翻了5倍，从客户咨询到成交转化全程自动化，团队可以专注于产品和策略。",
      avatar: "👨‍💼",
    },
    {
      name: "王芳",
      title: "母婴用品店主",
      content:
        "24/7不间断的智能客服让我们再也不错过任何一个客户，AI员工比人工客服更懂我们的产品，转化效果惊人。",
      avatar: "👩‍💻",
    },
    {
      name: "张强",
      title: "食品电商运营总监",
      content:
        "通过AI员工的精准推荐和个性化服务，我们的客单价提升了40%，复购率达到了65%，私域价值被完全激活。",
      avatar: "👨‍💻",
    },
  ];

  useEffect(() => {
    if (!isAutoPlay) return;

    const interval = setInterval(() => {
      setCurrentCase((prev) => (prev + 1) % cases.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlay, cases.length]);

  return (
    <section id="cases" className="cases-section">
      <div className="cases-content">
        <h2 className="cases-title">AI员工的真实效果</h2>

        <div
          className="cases-carousel"
          onMouseEnter={() => setIsAutoPlay(false)}
          onMouseLeave={() => setIsAutoPlay(true)}
        >
          <div className="case-main">
            <div className="case-avatar">{cases[currentCase].avatar}</div>
            <blockquote className="case-quote">
              "{cases[currentCase].content}"
            </blockquote>
            <div className="case-author">
              <div className="author-name">{cases[currentCase].name}</div>
              <div className="author-title">{cases[currentCase].title}</div>
            </div>
          </div>

          <div className="case-indicators">
            {cases.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentCase ? "active" : ""}`}
                onClick={() => setCurrentCase(index)}
              />
            ))}
          </div>
        </div>

        <div className="cases-stats">
          <div className="stat-item">
            <div className="stat-value">98%</div>
            <div className="stat-label">客户满意度</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">5倍</div>
            <div className="stat-label">转化效率</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">24/7</div>
            <div className="stat-label">全天服务</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">10万+</div>
            <div className="stat-label">服务客户</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 联系我们组件
interface ContactSectionProps {
  onShowQRCode: () => void;
}

const ContactSection: React.FC<ContactSectionProps> = ({ onShowQRCode }) => {
  return (
    <section id="contact" className="contact-section">
      <div className="contact-content">
        <h2 className="contact-title">准备好迎接 AI员工 了吗？</h2>

        <div className="contact-features">
          <div className="contact-feature">
            <div className="feature-icon">🚀</div>
            <div className="feature-text">立即上岗</div>
          </div>
          <div className="contact-feature">
            <div className="feature-icon">💰</div>
            <div className="feature-text">降本增效</div>
          </div>
          <div className="contact-feature">
            <div className="feature-icon">🔒</div>
            <div className="feature-text">安全可靠</div>
          </div>
        </div>

        <div className="contact-form-section">
          <h3>获取最新AI员工报价</h3>

          <div className="contact-form">
            <button
              type="button"
              className="submit-button"
              onClick={onShowQRCode}
            >
              立即咨询
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// 页脚组件
const Footer: React.FC = () => {
  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-info">
          <p>© 2025 北京黑夜智能科技有限公司 版权所有</p>
          <p>京ICP备2025118764号</p>
          <p>京公网安备11010802045627号</p>
        </div>
      </div>
    </footer>
  );
};

// 主应用组件
const App: React.FC = () => {
  const [showChat, setShowChat] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("表单提交:", formData);
    setShowContactForm(false);
    setFormData({ name: "", email: "", phone: "", message: "" });
  };

  // 显示企微二维码
  const showWeChatQR = () => {
    setShowQRCode(true);
  };

  return (
    <div className="App">
      <Header onShowQRCode={showWeChatQR} />
      <main>
        <HeroSection
          onStartChat={() => setShowChat(true)}
          onShowQRCode={showWeChatQR}
        />
        <SolutionSection />
        <ProductSection
          onStartChat={() => setShowChat(true)}
          onShowQRCode={showWeChatQR}
        />
        <FeaturesSection onShowQRCode={showWeChatQR} />
        <ScenarioSection />
        <CasesSection />
        <ContactSection onShowQRCode={showWeChatQR} />
      </main>

      <Footer />

      <div className="floating-buttons">
        <button
          className="floating-btn chat-btn"
          onClick={() => setShowChat(true)}
        >
          AI客服演示
        </button>
        <button className="floating-btn contact-btn" onClick={showWeChatQR}>
          立即体验
        </button>
      </div>

      {showContactForm && (
        <div
          className="modal-overlay"
          onClick={() => setShowContactForm(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowContactForm(false)}
            >
              ×
            </button>
            <h3>联系我们</h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="您的姓名"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
              <input
                type="email"
                placeholder="您的邮箱"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
              <input
                type="tel"
                placeholder="您的电话"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                required
              />
              <textarea
                placeholder="您的需求描述"
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                required
              />
              <button type="submit">提交</button>
            </form>
            <div className="contact-card-container">
              <ContactCard />
            </div>
          </div>
        </div>
      )}

      {showQRCode && (
        <div className="modal-overlay" onClick={() => setShowQRCode(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <ContactCard onClose={() => setShowQRCode(false)} />
          </div>
        </div>
      )}

      {showChat && (
        <div className="modal-overlay" onClick={() => setShowChat(false)}>
          <div
            className="chat-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <ChatInterface onClose={() => setShowChat(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
